import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";

export interface DashboardStats {
  totalJobs: number;
  activeJobs: number;
  totalApplications: number;
  newApplicationsThisMonth: number;
  conversionRate: number;
  hiredCount: number;
  lastMonthApplications: number;
  lastMonthHires: number;
}

export interface MonthlyStat {
  month: string;
  applications: number;
  hires: number;
}

export interface TopJob {
  id: string;
  title: string;
  apps: number;
  trend: "up" | "down" | "stable";
}

export interface RecentActivityItem {
  id: string;
  type: "new_application" | "job_expiring" | "system";
  message: string;
  time: string;
  icon: string;
}

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Vừa xong";
  if (diffMins < 60) return `${diffMins} phút trước`;
  if (diffHours < 24) return `${diffHours} giờ trước`;
  return `${diffDays} ngày trước`;
}

function getDaysUntil(date: Date): number {
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  return Math.ceil(diffMs / 86400000);
}

export function useDashboardStats() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    newApplicationsThisMonth: 0,
    conversionRate: 0,
    hiredCount: 0,
    lastMonthApplications: 0,
    lastMonthHires: 0,
  });
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStat[]>([]);
  const [topJobs, setTopJobs] = useState<TopJob[]>([]);
  const [recentActivities, setRecentActivities] = useState<RecentActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchStats = useCallback(async (silent = false) => {
    if (!user) {
      if (!silent) setLoading(false);
      return;
    }

    if (!silent) {
      setLoading(true);
    }
    setError(null);

    try {
      const { data: jobs, error: jobsError } = await supabase
        .from("vietnamcv_jobs")
        .select("id, title, status, posted_at, expiry_date")
        .eq("owner_id", user.id);

      if (jobsError) throw jobsError;

      const jobIds = (jobs || []).map((j) => j.id);
      const activeJobsCount = (jobs || []).filter((j) => j.status === "approved").length;

      if (jobIds.length === 0) {
        setStats({
          totalJobs: 0,
          activeJobs: 0,
          totalApplications: 0,
          newApplicationsThisMonth: 0,
          conversionRate: 0,
          hiredCount: 0,
          lastMonthApplications: 0,
          lastMonthHires: 0,
        });
        setMonthlyStats([]);
        setTopJobs([]);
        setRecentActivities([]);
        setLastUpdated(new Date());
        if (!silent) setLoading(false);
        return;
      }

      const { data: applications, error: appsError } = await supabase
        .from("vietnamcv_applications")
        .select("id, job_id, status, applied_at")
        .in("job_id", jobIds);

      if (appsError) throw appsError;

      const apps = applications || [];
      const totalApps = apps.length;
      const hiredCount = apps.filter((a) => a.status === "accepted").length;

      const now = new Date();
      const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();

      const newThisMonth = apps.filter((a) => a.applied_at >= startOfThisMonth).length;
      const lastMonthApps = apps.filter(
        (a) => a.applied_at >= startOfLastMonth && a.applied_at < startOfThisMonth
      ).length;
      const lastMonthHires = apps.filter(
        (a) =>
          a.status === "accepted" &&
          a.applied_at >= startOfLastMonth &&
          a.applied_at < startOfThisMonth
      ).length;

      const conversionRate =
        totalApps > 0 ? Math.round((hiredCount / totalApps) * 1000) / 10 : 0;

      setStats({
        totalJobs: (jobs || []).length,
        activeJobs: activeJobsCount,
        totalApplications: totalApps,
        newApplicationsThisMonth: newThisMonth,
        conversionRate,
        hiredCount,
        lastMonthApplications: lastMonthApps,
        lastMonthHires,
      });

      // Monthly stats — last 6 months
      const months = [
        "T1", "T2", "T3", "T4", "T5", "T6",
        "T7", "T8", "T9", "T10", "T11", "T12",
      ];

      const monthlyMap: Record<string, { applications: number; hires: number }> = {};
      apps.forEach((app) => {
        const d = new Date(app.applied_at);
        const key = months[d.getMonth()];
        if (!monthlyMap[key]) monthlyMap[key] = { applications: 0, hires: 0 };
        monthlyMap[key].applications++;
        if (app.status === "accepted") monthlyMap[key].hires++;
      });

      const currentMonth = now.getMonth();
      const monthlyStatsArr: MonthlyStat[] = [];
      for (let i = 5; i >= 0; i--) {
        let monthIdx = currentMonth - i;
        if (monthIdx < 0) monthIdx += 12;
        const key = months[monthIdx];
        monthlyStatsArr.push({
          month: key,
          applications: monthlyMap[key]?.applications || 0,
          hires: monthlyMap[key]?.hires || 0,
        });
      }
      setMonthlyStats(monthlyStatsArr);

      // Top performing jobs
      const appCountByJob: Record<string, number> = {};
      apps.forEach((app) => {
        appCountByJob[app.job_id] = (appCountByJob[app.job_id] || 0) + 1;
      });

      const topJobsArr: TopJob[] = (jobs || [])
        .map((job) => ({
          id: job.id,
          title: job.title,
          apps: appCountByJob[job.id] || 0,
          trend: "stable" as const,
        }))
        .sort((a, b) => b.apps - a.apps)
        .slice(0, 4);

      if (topJobsArr.length >= 2) {
        topJobsArr.forEach((job, idx) => {
          if (idx === 0 && job.apps > (topJobsArr[1]?.apps || 0) * 1.4) {
            job.trend = "up";
          } else if (idx === topJobsArr.length - 1 && job.apps > 0 && job.apps < (topJobsArr[0]?.apps || 0) * 0.4) {
            job.trend = "down";
          }
        });
      }

      setTopJobs(topJobsArr);

      // Recent activities
      const recentApps = [...apps]
        .sort((a, b) => new Date(b.applied_at).getTime() - new Date(a.applied_at).getTime())
        .slice(0, 4);

      const activities: RecentActivityItem[] = recentApps.map((app) => {
        const jobTitle = (jobs || []).find((j) => j.id === app.job_id)?.title || "vị trí tuyển dụng";
        return {
          id: app.id,
          type: "new_application",
          message: `Có ứng viên mới ứng tuyển vào vị trí ${jobTitle}`,
          time: getTimeAgo(new Date(app.applied_at)),
          icon: "ri-user-add-line",
        };
      });

      // Expiring jobs
      const soon = new Date();
      soon.setDate(soon.getDate() + 7);
      const expiringJobs = (jobs || []).filter((j) => {
        if (!j.expiry_date) return false;
        const exp = new Date(j.expiry_date);
        return exp <= soon && exp >= now;
      });

      expiringJobs.slice(0, 2).forEach((job) => {
        activities.push({
          id: `exp-${job.id}`,
          type: "job_expiring",
          message: `Tin ${job.title} sắp hết hạn`,
          time: `Còn ${getDaysUntil(new Date(job.expiry_date!))} ngày`,
          icon: "ri-timer-line",
        });
      });

      setRecentActivities(activities.slice(0, 6));
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không thể tải dữ liệu thống kê");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Auto-refresh every 30 seconds (silent — no skeleton flash)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchStats(true);
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchStats]);

  return { stats, monthlyStats, topJobs, recentActivities, loading, error, lastUpdated, refetch: fetchStats };
}